import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import CommunityHeader from '../components/public/CommunityHeader';
import CommunityTabs from '../components/public/CommunityTabs';
import UpdatesTab from '../components/public/UpdatesTab';
import EventsTab from '../components/public/EventsTab';
import ClassesTab from '../components/public/ClassesTab';
import PrayerTab from '../components/public/PrayerTab';
import DonateModal from '../components/public/DonateModal';
import AppDownloadModal from '../components/modals/AppDownloadModal';
import MobileAppPromptModal from '../components/modals/MobileAppPromptModal';
import '../styles/PublicCommunity.css';
import config from '../config';

/**
 * PublicCommunityPage Component
 * Main public-facing community page with tabs
 */
const PublicCommunityPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const scrollRef = useRef(null);
  
  const [community, setCommunity] = useState(null);
  const [groupId, setGroupId] = useState(id); // resolved UUID, initially the URL param
  const [activeTab, setActiveTab] = useState(0);
  const [isTabsSticky, setIsTabsSticky] = useState(false);
  const [isUserMember, setIsUserMember] = useState(false);
  const [membershipStatus, setMembershipStatus] = useState('not-member');
  const [previewOverrides, setPreviewOverrides] = useState({ name: null, logo: null });
  
  // Listen for preview updates from parent window
  useEffect(() => {
    const handleMessage = (event) => {
      // Verify message is from same origin for security
      if (event.origin !== window.location.origin) return;
      
      if (event.data.type === 'PREVIEW_UPDATE') {
        setPreviewOverrides({
          name: event.data.name,
          logo: event.data.logo
        });
      }
    };
    
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);
  
  // Update community when preview overrides change
  useEffect(() => {
    if (community && previewOverrides.name !== null) {
      setCommunity(prev => ({
        ...prev,
        name: previewOverrides.name || "Your Community",
        logo_url: previewOverrides.logo || null
      }));
    }
  }, [previewOverrides]);
  
  // Tab content state
  const [posts, setPosts] = useState([]);
  const [pinnedPosts, setPinnedPosts] = useState([]);
  const [events, setEvents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [weeklyAgendaItems, setWeeklyAgendaItems] = useState({});
  
  // Modal state
  const [showDonateModal, setShowDonateModal] = useState(false);
  const [showAppDownloadModal, setShowAppDownloadModal] = useState(false);
  const [showMobilePrompt, setShowMobilePrompt] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Check if mobile and show app prompt on first visit
  useEffect(() => {
    // Check for preview mode parameters from hash URL
    const hashParts = window.location.hash.split('?');
    const queryString = hashParts.length > 1 ? hashParts[1] : '';
    const urlParams = new URLSearchParams(queryString);
    const isPreviewMode = urlParams.get('preview') === 'true';
    
    // Don't show prompt in preview mode
    if (isPreviewMode) return;
    
    // Check if mobile device
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    const hasSeenPrompt = localStorage.getItem(`mobilePrompt_${id}`);
    
    if (isMobile && !hasSeenPrompt && community) {
      // Show prompt after a short delay for better UX
      const timer = setTimeout(() => {
        setShowMobilePrompt(true);
        localStorage.setItem(`mobilePrompt_${id}`, 'true');
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [id, community]);

  // Fetch community data
  useEffect(() => {
    const fetchCommunityData = async () => {
      try {
        setLoading(true);
        
        // Check for preview mode parameters from hash URL
        // Extract query string from hash (e.g., #/community/id?preview=true&name=...)
        const hashParts = window.location.hash.split('?');
        const queryString = hashParts.length > 1 ? hashParts[1] : '';
        const urlParams = new URLSearchParams(queryString);
        const isPreviewMode = urlParams.get('preview') === 'true';
        const previewName = urlParams.get('name');
        const previewLogo = urlParams.get('logo');
        const previewTab = urlParams.get('tab');
        const refreshParam = urlParams.get('_refresh'); // Cache-busting parameter
        
        console.log('Preview mode:', isPreviewMode);
        console.log('Preview name:', previewName);
        console.log('Preview logo:', previewLogo);
        console.log('Refresh param:', refreshParam);
        
        // Set active tab if specified in URL — supports both numeric (legacy preview) and named tab
        if (previewTab) {
          const TAB_NAME_MAP = { updates: 0, events: 1, classes: 2, minyanim: 3, prayer: 3 };
          const namedIdx = TAB_NAME_MAP[previewTab.toLowerCase()];
          if (namedIdx !== undefined) {
            setActiveTab(namedIdx);
          } else {
            const numIdx = parseInt(previewTab, 10);
            if (!isNaN(numIdx)) setActiveTab(numIdx);
          }
        }
        
        // Fetch community details - use public endpoint with cache-busting
        const cacheBuster = refreshParam || Date.now();
        const communityResponse = await fetch(`${config.API_URL}/groups/${id}/public?_t=${cacheBuster}`, {
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        });
        if (!communityResponse.ok) {
          throw new Error('Community not found');
        }
        const communityResult = await communityResponse.json();
        // Extract data from response (backend returns { success: true, data: {...} })
        const communityData = communityResult.data || communityResult;
        
        // Apply preview overrides if in preview mode
        if (isPreviewMode) {
          // Use postMessage overrides if available, otherwise use URL params as fallback
          communityData.name = previewOverrides.name || previewName || "Your Community";
          communityData.logo_url = previewOverrides.logo || previewLogo || null;
        }
        
        setCommunity(communityData);
        
        // Store the resolved UUID so sub-requests always use the actual group ID
        const resolvedId = communityData.id || id;
        setGroupId(resolvedId);
        
        // Check membership status (if logged in)
        const token = localStorage.getItem('token');
        // Note: Membership status check can be added later if needed
        // For now, public view doesn't require membership verification
        
        // Fetch tab content based on membership
        await fetchTabContent(cacheBuster, resolvedId);
        
      } catch (err) {
        console.error('Error fetching community:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchCommunityData();
  }, [id]); // Only depend on id, URL params are read fresh each time
  
  // Fetch tab content
  const fetchTabContent = async (cacheBuster = Date.now(), resolvedGroupId = groupId) => {
    const token = localStorage.getItem('token');
    const headers = {
      'Cache-Control': 'no-cache',
      'Pragma': 'no-cache'
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    try {
      // Fetch posts - public endpoint, no auth required
      const postsResponse = await fetch(`${config.API_URL}/groups/${resolvedGroupId}/posts/public?_t=${cacheBuster}`, { headers });
      if (postsResponse.ok) {
        const postsResult = await postsResponse.json();
        const postsData = postsResult.data || postsResult;
        setPinnedPosts(postsData.filter(p => p.is_pinned));
        setPosts(postsData.filter(p => !p.is_pinned));
      }
      
      // Fetch events - use public route (no auth or API key required)
      try {
        const eventsResponse = await fetch(`${config.API_URL}/groups/${resolvedGroupId}/events/public?_t=${cacheBuster}`, { headers });
        if (eventsResponse.ok) {
          const eventsResult = await eventsResponse.json();
          const eventsData = eventsResult.data || eventsResult;
          setEvents(eventsData);
        }
      } catch (err) {
        console.error('Error fetching events:', err);
      }
      
      // Fetch classes - use public route (no auth or API key required)
      try {
        const classesResponse = await fetch(`${config.API_URL}/groups/${resolvedGroupId}/classes/public?_t=${cacheBuster}`, { headers });
        if (classesResponse.ok) {
          const classesResult = await classesResponse.json();
          const classesData = classesResult.data || classesResult;
          setClasses(classesData);
        }
      } catch (err) {
        console.error('Error fetching classes:', err);
      }
      
      // Fetch agenda items - use public route (no auth or API key required)
      try {
        const agendaResponse = await fetch(`${config.API_URL}/groups/${resolvedGroupId}/agenda/public?_t=${cacheBuster}`, { headers });
        if (agendaResponse.ok) {
          const agendaResult = await agendaResponse.json();
          // Handle both array response and {data: [...]} response
          const agendaData = Array.isArray(agendaResult) ? agendaResult : (agendaResult.data || []);
          // Group by day of week
          const grouped = agendaData.reduce((acc, item) => {
            if (!acc[item.day_of_week]) {
              acc[item.day_of_week] = [];
            }
            acc[item.day_of_week].push(item);
            return acc;
          }, {});
          setWeeklyAgendaItems(grouped);
        }
      } catch (err) {
        console.error('Error fetching agenda:', err);
      }
    } catch (err) {
      console.error('Error fetching tab content:', err);
    }
  };
  
  // Handle scroll to detect sticky tabs
  useEffect(() => {
    const handleScroll = () => {
      if (scrollRef.current) {
        const scrollPosition = scrollRef.current.scrollTop;
        setIsTabsSticky(scrollPosition > 300);
      }
    };
    
    const scrollElement = scrollRef.current;
    if (scrollElement) {
      scrollElement.addEventListener('scroll', handleScroll);
      return () => scrollElement.removeEventListener('scroll', handleScroll);
    }
  }, []);
  
  const handleJoinClick = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      // Redirect to login
      navigate('/login');
      return;
    }
    
    try {
      const response = await fetch(`${config.API_URL}/groups/${groupId}/join`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.requires_approval) {
          setMembershipStatus('pending');
          alert('Your request to join has been sent for approval.');
        } else {
          setIsUserMember(true);
          setMembershipStatus('member');
          alert('Successfully joined the community!');
          // Refresh content
          await fetchTabContent();
        }
      } else {
        throw new Error('Failed to join community');
      }
    } catch (err) {
      console.error('Error joining community:', err);
      alert('Failed to join community. Please try again.');
    }
  };
  
  const handleDonateClick = () => {
    setShowDonateModal(true);
  };
  
  const handleGetAppClick = () => {
    setShowAppDownloadModal(true);
  };
  
  const handleCalendarClick = () => {
    // Check if mobile view (less than 1024px)
    const isMobile = window.innerWidth < 1024;
    if (isMobile) {
      setShowAppDownloadModal(true);
    } else {
      navigate(`/group/${id}/calendar`);
    }
  };
  
  const handleZmanimClick = () => {
    // Check if mobile view (less than 1024px)
    const isMobile = window.innerWidth < 1024;
    if (isMobile) {
      setShowAppDownloadModal(true);
    } else {
      // Open Zmanim modal (to be implemented)
      alert('Zmanim modal coming soon!');
    }
  };
  
  const handleSearchClick = () => {
    // Check if mobile view (less than 1024px)
    const isMobile = window.innerWidth < 1024;
    if (isMobile) {
      setShowAppDownloadModal(true);
    } else {
      // Open search modal (to be implemented)
      alert('Search modal coming soon!');
    }
  };
  
  const renderTabContent = () => {
    switch (activeTab) {
      case 0:
        return (
          <UpdatesTab
            community={community}
            posts={posts}
            pinnedPosts={pinnedPosts}
            isUserMember={isUserMember}
          />
        );
      case 1:
        return (
          <EventsTab
            community={community}
            events={events}
            isUserMember={isUserMember}
          />
        );
      case 2:
        return (
          <ClassesTab
            community={community}
            classes={classes}
            isUserMember={isUserMember}
          />
        );
      case 3:
        return (
          <PrayerTab
            community={community}
            weeklyAgendaItems={weeklyAgendaItems}
            isUserMember={isUserMember}
          />
        );
      default:
        return null;
    }
  };
  
  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div>Loading...</div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div>{error}</div>
      </div>
    );
  }
  
  if (!community) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div>Community not found</div>
      </div>
    );
  }
  
  return (
    <div className="public-community-page">
      <div className="public-community-scroll" ref={scrollRef}>
        <CommunityHeader
          community={community}
          isUserMember={isUserMember}
          membershipStatus={membershipStatus}
          onJoinClick={handleJoinClick}
          onDonateClick={handleDonateClick}
          onGetAppClick={handleGetAppClick}
          onCalendarClick={handleCalendarClick}
          onZmanimClick={handleZmanimClick}
          onSearchClick={handleSearchClick}
          showBackButton={false}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />
        
        <CommunityTabs
          activeTab={activeTab}
          onTabChange={setActiveTab}
          isSticky={isTabsSticky}
        />
        
        <div className="community-content">
          {renderTabContent()}
        </div>
      </div>
      
      {showDonateModal && (
        <DonateModal
          community={community}
          onClose={() => setShowDonateModal(false)}
        />
      )}
      
      <AppDownloadModal
        isOpen={showAppDownloadModal}
        onClose={() => setShowAppDownloadModal(false)}
        title="Get the Yesod App"
        message="Download the app to access all features"
      />
    </div>
  );
};

export default PublicCommunityPage;
